import {
    createStore,
    applyMiddleware,
    compose
} from 'redux';
import {
    connectRouter,
    routerMiddleware
} from 'connected-react-router';
import {
    createBrowserHistory,
    createMemoryHistory
} from 'history';
import reducers from './reducers';
import reduxThunk from 'redux-thunk';
import axios from 'axios';
import get from 'lodash/get';

import { initialize, addTranslation, setActiveLanguage } from 'react-localize-redux';

import GlobalLangFile from './lang/global_lang';
import KBankLangFile from './lang/kbank_lang';
import ERRLangFile from './lang/err_lang';
import BloggerLangFile from './lang/blogger_lang';
import The1LangFile from './lang/the1_lang'
import BranchLangFile from './lang/branches_lang'
import { DEAUTH } from './actions/auth/type';

// Import centralized config
import { ROOT_URL, API_URL, STRAPI_API } from './config';

// A nice helper to tell us if we're on the server
export const isServer = !(
    typeof window !== 'undefined' &&
    window.document &&
    window.document.createElement
);

/**
 * Creates and configures a Redux store, history object, and Axios instances for use in a React application.
 *
 * @param {Object|null} req - The request object, typically provided on the server-side. Used to extract cookies for server-side requests.
 * @param {string} [url='/'] - The initial URL for the history object.
 * @returns {Object} An object containing the configured Redux store, history object, and Axios instances.
 * @property {Object} store - The configured Redux store.
 * @property {Object} history - The history object for navigation.
 * @property {Object} axInstance - The primary Axios instance with interceptors for loading state management.
 * @property {Object} axInstanceNotLoading - An Axios instance without loading state management interceptors.
 */
export default (req = null, url = '/') => {
    // Create a history depending on the environment
    const history = isServer
        ? createMemoryHistory({ initialEntries: [encodeURIComponent(url)] })
        : createBrowserHistory();

    const enhancers = [];

    // Dev tools are helpful
    if (process.env.NODE_ENV === 'development' && !isServer) {
        const devToolsExtension = window.__REDUX_DEVTOOLS_EXTENSION__;

        if (typeof devToolsExtension === 'function') {
            enhancers.push(devToolsExtension());
        }
    }
    // Use centralized config for API URLs - ensures consistent environment handling
    // For server-side rendering, use direct API URL instead of proxy
    const baseURL = isServer ? ROOT_URL : API_URL;

    const axOptions = {
        baseURL: baseURL,
        withCredentials: !isServer, // Only for client-side
    };

    if (isServer) {
        if (req && req.get('cookie')) {
            axOptions.headers = { cookie: req.get('cookie') }
        }
        // Don't use credentials for server-side requests
        delete axOptions.withCredentials;
    }

    const axInstance = axios.create(axOptions);
    const axInstanceNotLoading = axios.create(axOptions);
    const axStrapi = axios.create({
        baseURL: STRAPI_API,
    });

    // Log axios configuration for debugging (development only)
    if (process.env.NODE_ENV === 'development') {
        console.log('🌐 Axios Configuration:');
        console.log('  Server Mode:', isServer);
        console.log('  Selected baseURL:', baseURL);
        console.log('  Main API (axios):', axOptions.baseURL);
        console.log('  Background API (axNL):', axOptions.baseURL);
        console.log('  Strapi CMS (axStrapi):', STRAPI_API);
        console.log('---');
    }
    const middleware = [reduxThunk.withExtraArgument({ axios: axInstance, axNL: axInstanceNotLoading, axStrapi: axStrapi }), routerMiddleware(history)];
    const composedEnhancers = compose(
        applyMiddleware(...middleware),
        ...enhancers
    );

    // Do we have preloaded state available? Great, save it.
    const initialState = !isServer ? window.__PRELOADED_STATE__ : {};
    // Delete it once we have it stored in a variable
    if (!isServer) {
        delete window.__PRELOADED_STATE__;
    }
    // Create the store
    const store = createStore(
        connectRouter(history)(reducers),
        initialState,
        composedEnhancers
    );

    axInstance.interceptors.request.use((config) => {
        if (!isServer) {
            const token = localStorage.getItem('token');
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }
        }
        store.dispatch({
            type: 'LOADING',
            payload: true
        });
        return config;
    }, (error) => {
        // Do something with request error
        store.dispatch({
            type: 'LOADING',
            payload: false
        });
        return Promise.reject(error);
    });

    axInstance.interceptors.response.use((response) => {
        store.dispatch({
            type: 'LOADING',
            payload: false
        });
        return response;
    }, (error) => {
        // Do something with request error
        if (error.response && error.response.status === 401) {
            store.dispatch({
                type: DEAUTH
            });
        }
        store.dispatch({
            type: 'LOADING',
            payload: false
        });
        return Promise.reject(error);
    });

    axInstanceNotLoading.interceptors.response.use((response) => {
        return response;
    }, (error) => {
        // Do something with request error
        if (get(error, "response.status", 401) === 401) {
            store.dispatch({
                type: DEAUTH
            });
        }
        return Promise.reject(error);
    })

    let langSetting = 'th';
    const languages = [
        { name: 'ภาษาไทย', code: 'th', flag: 'https://cdn.thebeautrium.com/web-image/thai-flag-small.png' },
        { name: 'English', code: 'en', flag: 'https://cdn.thebeautrium.com/web-image/uk-flag-small.png' }
    ];
    store.dispatch(initialize(languages));
    store.dispatch(addTranslation(GlobalLangFile));
    store.dispatch(addTranslation(KBankLangFile));
    store.dispatch(addTranslation(ERRLangFile));
    store.dispatch(addTranslation(BloggerLangFile));
    store.dispatch(addTranslation(The1LangFile))
    store.dispatch(addTranslation(BranchLangFile));
    store.dispatch(setActiveLanguage(langSetting));

    return {
        store,
        history,
        axInstance,
        axInstanceNotLoading
    };
};